/**
 * プロダクト一括ダウンロードバッジ
 *
 * プロダクトカード（ComicItem）左下に表示するコンパクトバッジ。
 * タップで全エピソード（audio + cue + pdf）を一括ダウンロードし、
 * 全エピソードがオフライン保存済みの場合は完了アイコンを表示する。
 */
import { useCallback, useMemo } from 'react'

import type { EpisodeList } from '@/api/type'
import { CompactDownloadBadge } from '@/components/shared/compact-download-badge'
import { useProductDownload } from '@/hooks/useEpisodeDownload'
import { useHasActiveDownload, useIsProductOffline, useRemoveProductOffline } from '@/hooks/useProductDownloadStatus'
import { buildDownloadItems } from '@/lib/download-utils'
import { cn } from '@/lib/utils'
import { useDownloadStore } from '@/store/useDownloadStore'

type Props = {
  episodes: EpisodeList[]
  productId: number
  domain: string
  /** バッジの配置を上書きするクラス（既定は左下固定） */
  className?: string
}

export const ProductDownloadBadge = ({ episodes, productId, domain, className }: Props) => {
  const { downloadProduct } = useProductDownload()
  const episodeIds = useMemo(() => episodes.map((ep) => ep.episode_id), [episodes])
  const downloads = useDownloadStore((s) => s.downloads)

  const hasActiveDownload = useHasActiveDownload(episodeIds)
  const { isFullyOffline, setIsFullyOffline } = useIsProductOffline(episodeIds)
  const removeOffline = useRemoveProductOffline(productId, setIsFullyOffline)

  // 全エピソードの平均ダウンロード進捗を計算
  const averageProgress = useMemo(() => {
    if (!hasActiveDownload || episodeIds.length === 0) return 0
    const totalProgress = episodeIds.reduce((sum, id) => {
      const dl = downloads.get(id)
      return sum + (dl?.progress ?? 0)
    }, 0)
    return Math.round(totalProgress / episodeIds.length)
  }, [hasActiveDownload, episodeIds, downloads])

  const handleDownload = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      downloadProduct(buildDownloadItems(episodes, productId, domain))
    },
    [episodes, productId, domain, downloadProduct],
  )

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      removeOffline()
    },
    [removeOffline],
  )

  const status = hasActiveDownload ? 'downloading' : isFullyOffline ? 'done' : 'idle'

  return (
    <CompactDownloadBadge
      status={status}
      progress={averageProgress}
      onDownload={handleDownload}
      onRemove={handleRemove}
      className={cn('z-10', className)}
    />
  )
}
